import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import { AutoComplete, Button, Input, Table, Tag, notification, Tooltip } from 'antd'; // CHANGED: Added AutoComplete
import dayjs from 'dayjs';

import { BiExport } from 'react-icons/bi';
import { FaEye, FaSearch } from 'react-icons/fa';
import { FiFilter } from 'react-icons/fi';
import { HiOutlineRefresh } from 'react-icons/hi';

import '../../../css/UserAdmin/Global.css';
import Footer from '../Footer';
import Header from '../NewHeader';
import SideNav from '../SideNav';

const ManageStudents = () => {
    const navigate = useNavigate();
    const [isSideNavOpen, setIsSideNavOpen] = useState(false);
    const [isSmallScreen, setIsSmallScreen] = useState(window.innerWidth < 768);
    const [allEnrolledStudents, setAllEnrolledStudents] = useState([]); // Raw data from API
    const [dataSource, setDataSource] = useState([]); // Data for the table (filtered and sorted)
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [tableFilters, setTableFilters] = useState({}); // For AntD controlled table filters
    const [sorter, setSorter] = useState({});
    const [autocompleteOptions, setAutocompleteOptions] = useState([]); // ADDED: For AutoComplete suggestions

    const toggleSideNav = useCallback(() => setIsSideNavOpen(prev => !prev), []);

    useEffect(() => {
        const handleResize = () => setIsSmallScreen(window.innerWidth < 768);
        window.addEventListener('resize', handleResize);
        handleResize();
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const fetchEnrolledStudents = useCallback(async () => {
        setLoading(true);
        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/enrollments`);
            if (!response.ok) {
                const errorText = await response.text();
                throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`);
            }
            const result = await response.json();
            const studentsData = Array.isArray(result) ? result : (result.data || []);

            if (!Array.isArray(studentsData)) {
                console.error('Expected students data to be an array but got:', typeof studentsData, studentsData);
                throw new Error(`Expected array but got ${typeof studentsData}`);
            }
            // Ensure key is present for AntD table
            setAllEnrolledStudents(studentsData.map(student => ({ ...student, key: student._id || student.studentID })));
        } catch (error) {
            console.error("Error fetching enrolled students:", error);
            notification.error({ message: 'Fetch Error', description: 'Could not fetch student data.' });
            setAllEnrolledStudents([]);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchEnrolledStudents();
    }, [fetchEnrolledStudents]);

    // --- Client-Side Filtering and Sorting ---
    const filteredAndSortedStudents = useMemo(() => {
        let processedStudents = [...allEnrolledStudents];

        // Apply search term filter (this will be triggered by AutoComplete's onChange/onSelect)
        if (searchTerm) {
            const lowerSearchTerm = searchTerm.toLowerCase();
            // Simple .includes() search. For fuzzy, integrate Fuse.js here.
            processedStudents = processedStudents.filter(student =>
                (student.studentID || '').toLowerCase().includes(lowerSearchTerm) ||
                (student.fullName || '').toLowerCase().includes(lowerSearchTerm) ||
                (student.email || '').toLowerCase().includes(lowerSearchTerm) ||
                (student.academicLevel || '').toLowerCase().includes(lowerSearchTerm) ||
                (student.academicStrand || '').toLowerCase().includes(lowerSearchTerm) ||
                (student.section || '').toLowerCase().includes(lowerSearchTerm)
            );
        }

        if (tableFilters.enrollmentStatus && tableFilters.enrollmentStatus.length > 0) {
            processedStudents = processedStudents.filter(student =>
                tableFilters.enrollmentStatus.includes(student.enrollmentStatus)
            );
        }
        // Add more AntD filter logic here for other columns as needed

        if (sorter.columnKey && sorter.order) {
            processedStudents.sort((a, b) => {
                const valA = a[sorter.columnKey];
                const valB = b[sorter.columnKey];

                // Handle undefined or null values by treating them as empty strings or smallest/largest numbers
                const safeValA = valA === undefined || valA === null ? '' : valA;
                const safeValB = valB === undefined || valB === null ? '' : valB;
                
                let comparison = 0;
                if (typeof safeValA === 'string' && typeof safeValB === 'string') {
                    comparison = safeValA.localeCompare(safeValB);
                } else if (typeof safeValA === 'number' && typeof safeValB === 'number') {
                    comparison = safeValA - safeValB;
                } else if (dayjs(safeValA).isValid() && dayjs(safeValB).isValid()) { // Date sorting
                    comparison = dayjs(safeValA).unix() - dayjs(safeValB).unix();
                }
                // Fallback for mixed types or other scenarios
                else if (safeValA < safeValB) {
                    comparison = -1;
                } else if (safeValA > safeValB) {
                    comparison = 1;
                }
                
                return sorter.order === 'ascend' ? comparison : -comparison;
            });
        }
        return processedStudents;
    }, [allEnrolledStudents, searchTerm, tableFilters, sorter]);

    useEffect(() => {
        setDataSource(filteredAndSortedStudents);
    }, [filteredAndSortedStudents]);

    // Handler for AutoComplete's onChange and onSelect
    const handleSearchInputChange = (value) => {
        setSearchTerm(value || ''); // Ensure searchTerm is not undefined
    };

    // Handler for AutoComplete's onSearch (to generate suggestions)
    const handleAutocompleteSuggest = (searchText) => {
        if (!searchText) {
            setAutocompleteOptions([]);
            return;
        }
        const lowerSearchText = searchText.toLowerCase();
        const suggestions = allEnrolledStudents
            .flatMap(student => [ // Create an array of potential search strings from each student
                student.studentID,
                student.fullName,
                student.email,
                student.academicLevel,
                student.academicStrand,
                student.section
            ])
            .filter(Boolean) // Remove any null or undefined values
            .map(value => String(value)) // Ensure all are strings for comparison
            .filter((value, index, self) => self.map(v => v.toLowerCase()).indexOf(value.toLowerCase()) === index) // Unique, case-insensitive
            .filter(value => value.toLowerCase().includes(lowerSearchText))
            .map(value => ({ value: value, label: value })); // Format for AutoComplete

        setAutocompleteOptions(suggestions.slice(0, 10)); // Limit suggestions
    };

    const handleClearFilters = () => {
        setSearchTerm('');
        setTableFilters({});
        setSorter({});
        setAutocompleteOptions([]); // Clear autocomplete suggestions as well
        // The useMemo for filteredAndSortedStudents will re-calculate based on empty searchTerm.
        // If you want to force a re-fetch from API (e.g., if data might have changed server-side):
        // fetchEnrolledStudents();
    };

    const handleRefresh = () => {
        setSearchTerm(''); // Optionally clear search on refresh
        setTableFilters({});
        setSorter({});
        setAutocompleteOptions([]);
        fetchEnrolledStudents();
    };

        const handleExport = () => {
        const currentDate = new Date().toISOString().split('T')[0];
        const fileName = `student-records-report-${currentDate}.pdf`;
        const userID = localStorage.getItem('userID');
        const fullName = localStorage.getItem('fullName'); // This is the admin's full name
        const role = localStorage.getItem('role');       // This is the admin's role

        // The API call for export
        fetch(`${process.env.REACT_APP_API_URL}/api/admin/export/enrolled-students?userID=${encodeURIComponent(userID || '')}&fullName=${encodeURIComponent(fullName || '')}&role=${encodeURIComponent(role || '')}`, {
            method: 'GET',
        })
            .then(response => {
                if (!response.ok) {
                    // Try to get error message from response if possible
                    return response.text().then(text => { 
                        let errorMsg = 'Network response was not ok for student export.';
                        try {
                            const errorData = JSON.parse(text);
                            if (errorData && errorData.message) {
                                errorMsg = errorData.message;
                            }
                        } catch (e) {
                            // If response is not JSON, use the raw text or a default
                            if (text) errorMsg = text;
                        }
                        throw new Error(errorMsg);
                    });
                }
                return response.blob();
            })
            .then(blob => {
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', fileName);
                document.body.appendChild(link);
                link.click();
                link.remove();
                window.URL.revokeObjectURL(url);
                notification.success({
                    message: 'Export Successful',
                    description: `${fileName} has been downloaded.`,
                });

                // --- CORRECTED LOGGING ---
                const logData = {
                    userID: userID,         // Admin's User ID
                    accountName: fullName,  // Admin's Full Name
                    role: role,             // Admin's Role
                    action: 'Export',
                    detail: `Exported enrolled students report: ${fileName}` // Corrected detail
                };

                fetch(`${process.env.REACT_APP_API_URL}/api/admin/system-logs`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify(logData)
                })
                    .then(response => response.json())
                    .then(data => {
                        if (data.success) { // Assuming your log API returns a success flag
                            console.log('System log for student export recorded:', data);
                        } else {
                            console.warn('System log for student export failed to record properly:', data);
                        }
                    })
                    .catch(error => {
                        console.error('Failed to record system log for student export:', error);
                    });
                // --- END OF CORRECTED LOGGING ---
            })
            .catch(error => {
                console.error('Student Export failed:', error);
                notification.error({
                    message: 'Export Failed',
                    description: error.message || 'There was an error exporting the students report.',
                });
            });
    };

    const columns = [
        {
            title: 'Student ID',
            dataIndex: 'studentID',
            key: 'studentID',
            width: 130,
            sorter: true,
            sortOrder: sorter.columnKey === 'studentID' ? sorter.order : null,
        },
        {
            title: 'Full Name',
            dataIndex: 'fullName',
            key: 'fullName',
            width: 220,
            render: (text, record) => (
                <div>
                    <div style={{ fontWeight: 'bold' }}>{text || 'N/A'}</div>
                    {record.email && <div style={{ fontSize: '12px', color: '#666' }}>{record.email}</div>}
                </div>
            ),
            sorter: true,
            sortOrder: sorter.columnKey === 'fullName' ? sorter.order : null,
        },
        {
            title: 'Grade Level', // Renamed for clarity
            dataIndex: 'academicLevel',
            key: 'academicLevel',
            width: 120,
            sorter: true,
            sortOrder: sorter.columnKey === 'academicLevel' ? sorter.order : null,
        },
        {
            title: 'Strand', // Renamed for clarity
            dataIndex: 'academicStrand',
            key: 'academicStrand',
            width: 100,
            sorter: true,
            sortOrder: sorter.columnKey === 'academicStrand' ? sorter.order : null,
        },
        {
            title: 'Section', // Renamed for clarity
            dataIndex: 'section',
            key: 'section',
            width: 120,
            sorter: true, // Enable AntD sorter, will use the logic in useMemo
            sortOrder: sorter.columnKey === 'section' ? sorter.order : null,
        },
        {
            title: 'Enrollment Status', // Renamed for clarity
            dataIndex: 'enrollmentStatus',
            key: 'enrollmentStatus',
            width: 150,
            filters: [
                { text: 'Complete', value: 'Complete' },
                { text: 'Incomplete', value: 'Incomplete' },
                { text: 'Pending', value: 'Pending' },
                { text: 'Withdrawn', value: 'Withdrawn' },
                { text: 'Dropped', value: 'Dropped' },
            ],
            // onFilter is handled by the useMemo logic for tableFilters
            filteredValue: tableFilters.enrollmentStatus || null,
            render: (status) => {
                let color = 'default';
                if (status === 'Complete') color = 'green';
                else if (status === 'Incomplete') color = 'orange';
                else if (status === 'Pending') color = 'blue';
                else if (status === 'Withdrawn') color = 'red';
                else if (status === 'Dropped') color = 'volcano';
                return <Tag color={color}>{status || 'N/A'}</Tag>;
            },
            sorter: true,
            sortOrder: sorter.columnKey === 'enrollmentStatus' ? sorter.order : null,
        },
        {
            title: 'Enrollment Date', // Renamed for clarity
            dataIndex: 'enrolledDate', // Assuming this is the correct field from your API
            key: 'enrolledDate',
            width: 160,
            render: (text) => dayjs(text).isValid() ? dayjs(text).format('YYYY-MM-DD HH:mm') : 'N/A',
            sorter: true,
            sortOrder: sorter.columnKey === 'enrolledDate' ? sorter.order : null,
        },
        {
            title: 'Action',
            key: 'action',
            fixed: 'right',
            width: 150,
            align: 'center',
            render: (_, record) => (
                <Tooltip title="View Full Student Details">
                    <Button
                        icon={<FaEye />}
                        onClick={() => navigate(`/admin/manage-students/view/${record._id || record.key}`)}
                    >
                        View Details
                    </Button>
                </Tooltip>
            ),
        },
    ];

    return (
        <div className={`admin-page-container ${isSideNavOpen ? 'admin-sidenav-open' : ''}`}>
            <Header onMenuClick={toggleSideNav} isSideNavOpen={isSideNavOpen} />
            <div className="admin-content-wrapper">
                <div className="admin-sidenav-container">
                    <SideNav isOpen={isSideNavOpen} onClose={toggleSideNav} isSmallScreen={isSmallScreen} />
                </div>
                <main className="admin-main-content-area">
                    <div className="page-title">
                        <p className="heading">Manage Student Records</p>
                    </div>
                    <div className="table-functions">
                        <div className="left-tools">
                            <Button icon={<FiFilter />} onClick={handleClearFilters}>Clear Filters</Button>
                            <Button icon={<HiOutlineRefresh />} onClick={handleRefresh} loading={loading}>Refresh</Button>
                            <Button icon={<BiExport />} onClick={handleExport}>Export</Button>
                        </div>
                        <div className="right-tools">
                            <AutoComplete
                                value={searchTerm}
                                options={autocompleteOptions}
                                style={{ width: 350 }}
                                onSearch={handleAutocompleteSuggest} // Generates suggestions
                                onChange={handleSearchInputChange}    // Updates searchTerm on typing
                                onSelect={handleSearchInputChange}   // Updates searchTerm on selection
                                placeholder="Search ID, Name, Email, Level, Strand, Section..."
                                allowClear
                            >
                                <Input suffix={<FaSearch style={{ color: '#aaa' }} />} />
                            </AutoComplete>
                        </div>
                    </div>

                    <Table
                        style={{ width: '100%', flex: 1, marginTop: '16px' }}
                        columns={columns}
                        dataSource={dataSource} // dataSource is now the filteredAndSortedStudents
                        loading={loading}
                        bordered
                        scroll={{ x: 1500 }} // Adjust as needed based on column widths
                        pagination={{
                            showSizeChanger: true,
                            showQuickJumper: true,
                            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} students`,
                            pageSizeOptions: ['10', '20', '50', '100'],
                            defaultPageSize: 10, // Or your preferred default
                        }}
                        onChange={(pagination, filters, newSorter) => {
                            setTableFilters(filters);
                            setSorter(newSorter);
                            // No need to call setDataSource here, useMemo will handle it
                        }}
                    />
                </main>
            </div>
            <Footer />
        </div>
    );
};

export default ManageStudents;